import { apiClient } from '@/api/client';
import type { ApiResponse } from '@/types/api';

export type AiProviderType = 'GEMINI' | 'OPENAI' | 'CLAUDE';

export interface AiConfigData {
  enabled: boolean;
  provider: AiProviderType;
  model: string;
  hasApiKey: boolean;
  lastTestedAt?: string;
  lastTestStatus?: 'SUCCESS' | 'FAILED' | 'PENDING' | 'CONNECTED' | 'ERROR';
  lastTestLatencyMs?: number;
  totalTokensUsed: number;
  totalQueries: number;
  lastErrorMessage?: string;
}

export interface UpdateAiConfigPayload {
  enabled?: boolean;
  isEnabled?: boolean;
  provider?: AiProviderType;
  apiKey?: string;
  model?: string;
}

export interface AiTestResult {
  success: boolean;
  latencyMs: number;
  message: string;
  model?: string;
}

export interface ChatMessageItem {
  role: 'user' | 'assistant';
  content: string;
  toolData?: Record<string, any>;
  isDraftAction?: boolean;
}

export interface SendChatMessagePayload {
  message: string;
  conversationHistory?: ChatMessageItem[];
  includeReport?: boolean;
  reportFormat?: 'PDF' | 'XLSX' | 'CSV';
  conversationContext?: {
    activePropertyId?: string;
    activeCategory?: string;
    activePeriod?: string;
    activeVendorId?: string;
  };
}

export interface ChatToolExecutionItem {
  toolName: string;
  description: string;
  args: any;
  resultSummary: any;
}

export interface ChatResponseData {
  reply: string;
  toolsExecuted: (string | ChatToolExecutionItem)[];
  toolData?: Record<string, any>;
  reportDownloadUrl?: string;
  reportFormat?: 'PDF' | 'XLSX' | 'CSV';
  tokenUsage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  isRefusal?: boolean;
  denialReason?: string;
  isDraftAction?: boolean;
}

export interface AiInsightCard {
  id: string;
  category: 'COLLECTION_RISK' | 'EXPENSE_ALERT' | 'WATER_USAGE' | 'CASH_FLOW' | 'MAINTENANCE_SLA';
  title: string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL' | 'SUCCESS';
  metricHeadline: string;
  summary: string;
  asOf: string;
  deepLinkPath: string;
}

export interface AiHistoricalPrediction {
  _id: string;
  metric: string;
  forecastPeriod: string;
  predictedValue: number;
  lowerBound: number;
  upperBound: number;
  confidence: 'LOW' | 'MEDIUM' | 'HIGH';
  modelStrategy: string;
  explanation: string;
  actualValue?: number | null;
  accuracyScore?: number | null;
  createdAt: string;
}

export class AiService {
  static async getConfig(): Promise<ApiResponse<AiConfigData>> {
    const { data } = await apiClient.get<ApiResponse<AiConfigData>>('/society/ai/config');
    return data;
  }

  static async updateConfig(payload: UpdateAiConfigPayload): Promise<ApiResponse<AiConfigData>> {
    const normalized = {
      ...payload,
      isEnabled: payload.isEnabled !== undefined ? payload.isEnabled : payload.enabled,
    };
    const { data } = await apiClient.put<ApiResponse<AiConfigData>>('/society/ai/config', normalized);
    return data;
  }

  static async testConnection(payload?: UpdateAiConfigPayload): Promise<ApiResponse<AiTestResult>> {
    const { data } = payload
      ? await apiClient.post<ApiResponse<AiTestResult>>('/society/ai/config/test', payload)
      : await apiClient.post<ApiResponse<AiTestResult>>('/society/ai/config/test');
    return data;
  }

  static async sendMessage(payload: SendChatMessagePayload): Promise<ApiResponse<ChatResponseData>> {
    const { data } = await apiClient.post<ApiResponse<ChatResponseData>>('/society/ai/chat', payload);
    return data;
  }

  static async getInsights(): Promise<ApiResponse<AiInsightCard[]>> {
    const { data } = await apiClient.get<ApiResponse<AiInsightCard[]>>('/society/ai/insights');
    return data;
  }

  static async getPredictions(metric?: string): Promise<ApiResponse<AiHistoricalPrediction[]>> {
    const url = metric ? `/society/ai/predictions?metric=${encodeURIComponent(metric)}` : '/society/ai/predictions';
    const { data } = await apiClient.get<ApiResponse<AiHistoricalPrediction[]>>(url);
    return data;
  }

  static async evaluatePredictionAccuracy(id: string, actualValue: number): Promise<ApiResponse<any>> {
    const { data } = await apiClient.post<ApiResponse<any>>(`/society/ai/predictions/${id}/evaluate`, { actualValue });
    return data;
  }
}
